import { computed, type Ref } from "vue";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatDays(value: number): string {
  return `${Math.round(value)}d`;
}

export function useOpportunityDashboardMetrics(rawData: Ref<any | undefined>) {
  const metrics = computed(() => {
    const data = rawData.value;
    return [
      {
        id: 1,
        title: "Total Meetings Held",
        value: data?.totalMeetingsHeld || 0,
        changePercent: data?.totalMeetingsHeldChangePercent || 0,
      },
      {
        id: 2,
        title: "Total Pipeline Value (Weighted)",
        value: formatCurrency(data?.totalPipelineValue || 0),
        changePercent: data?.totalPipelineValueChangePercent || 0,
      },
      {
        id: 3,
        title: "Closed Won Revenue",
        value: formatCurrency(data?.closedWonRevenue || 0),
        changePercent: data?.closedWonRevenueChangePercent || 0,
      },
      {
        id: 4,
        title: "Win Rate",
        value: formatPercentage(data?.winRate || 0),
        changePercent: data?.winRateChangePercent || 0,
      },
      {
        id: 5,
        title: "Average Deal Size",
        value: formatCurrency(data?.averageDealSize || 0),
        changePercent: data?.averageDealSizeChangePercent || 0,
      },
      {
        id: 6,
        title: "Opportunities",
        value: data?.opportunities || 0,
        changePercent: data?.opportunitiesChangePercent || 0,
      },
      {
        id: 7,
        title: "Average Time to Win",
        value: formatDays(data?.averageTimeToWin || 0),
        changePercent: data?.averageTimeToWinChangePercent || 0,
      },
      {
        id: 8,
        title: "Sales Velocity",
        value: formatCurrency(data?.salesVelocity || 0),
        changePercent: data?.salesVelocityChangePercent || 0,
      },
    ];
  });

  const salesFunnel = computed(() => {
    const data = rawData.value;
    return Array.isArray(data?.salesFunnel) ? data.salesFunnel : [];
  });

  const salesFunnelCompare = computed(() => {
    const data = rawData.value;
    return Array.isArray(data?.salesFunnelCompare) ? data.salesFunnelCompare : [];
  });

  const revenueByLeadSourceData = computed(() => {
    const data = rawData.value;
    if (data?.revenueByLeadSource && Array.isArray(data.revenueByLeadSource)) {
      return data.revenueByLeadSource;
    }
    return [];
  });

  const repPerformanceData = computed(() => {
    const data = rawData.value;
    if (data?.repPerformance && Array.isArray(data.repPerformance)) {
      return data.repPerformance;
    }
    return [];
  });

  const stagePipelineData = computed(() => {
    const data = rawData.value;
    if (data?.stagePipeline && Array.isArray(data.stagePipeline)) {
      return data.stagePipeline.map((stage: any) => ({
        stageId: stage.stageId,
        stage: stage.stageName,
        color: stage.stageColor || "#FFC107",
        count: stage.count || 0,
        value: stage.value || 0,
        weightedValue: stage.weightedValue || 0,
        avgTimeToAdvance: Math.round(stage.avgTimeToAdvance || 0),
        conversionRate: stage.conversionRate || 0,
        lost: stage.lost || 0,
      }));
    }
    return [];
  });

  const pipelineByCloseDateData = computed(() => {
    const data = rawData.value;
    if (data?.pipelineByCloseDate) {
      return {
        dates: data.pipelineByCloseDate.dates || [],
        qualification: data.pipelineByCloseDate.qualification || [],
        proposal: data.pipelineByCloseDate.proposal || [],
        negotiation: data.pipelineByCloseDate.negotiation || [],
        closedWon: data.pipelineByCloseDate.closedWon || [],
        totalTrend: data.pipelineByCloseDate.totalTrend || [],
        counts: data.pipelineByCloseDate.counts || {},
      };
    }
    return {
      dates: [],
      qualification: [],
      proposal: [],
      negotiation: [],
      closedWon: [],
      totalTrend: [],
      counts: {},
    };
  });

  return {
    metrics,
    salesFunnel,
    salesFunnelCompare,
    revenueByLeadSourceData,
    repPerformanceData,
    stagePipelineData,
    pipelineByCloseDateData,
  };
}
